import { reactive, readonly } from 'vue';

// Same-origin, always-available, tiny resource used to actively verify that
// the network connection is truly usable (navigator.onLine only reflects the
// OS network adapter state, not real internet/server reachability).
const PROBE_URL = 'favicon.ico';
const PROBE_TIMEOUT_MS = 5000;
const AUTO_RETRY_INTERVAL_MS = 5000;
const HOME_PATH = '/';

const state = reactive({
  // True while the offline splash should be shown.
  isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
  // True while a retry probe is in flight (drives the retry button spinner).
  checking: false,
  // True when the cached home page ("/") is available, so the
  // "العودة للرئيسية" button can be shown even while offline.
  homeCached: false,
});

let router = null;
let autoRetryTimer = null;
let initialized = false;

async function probe() {
  if (typeof fetch !== 'function') return navigator.onLine;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

  try {
    await fetch(`${PROBE_URL}?_=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
    });
    return true;
  } catch (e) {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

// True while a confirmation probe (triggered by an axios network error) is
// in flight, so a burst of simultaneously-failing requests only fires one.
let confirming = false;

/**
 * A single axios request failing with "no response" is NOT proof the device
 * is offline. Inside the Flutter WebView wrapper the app now signs every
 * cross-origin api.moustafahosney.com request with X-Device-* and X-Signature headers;
 * if one of those requests is rejected at the network layer (e.g. a CORS
 * preflight for the custom headers, a dropped keep-alive, a single 5xx with
 * no CORS header), that must NOT black out the whole app.
 *
 * So instead of trusting one failed cross-origin request, we verify real
 * reachability against a SAME-ORIGIN probe (favicon.ico). Only if the origin
 * that actually served this SPA is unreachable do we declare offline. This is
 * what stops the "load -> Offline -> auto reload -> load -> Offline" loop:
 * the server is up, the probe succeeds, and the failed signed request is
 * left to the app's normal per-request error handling.
 */
async function confirmOfflineFromNetworkError() {
  if (state.isOffline || confirming) return;
  confirming = true;
  try {
    const reachable = await probe();
    if (!reachable) {
      // eslint-disable-next-line no-console
      console.warn('[OfflineManager] origin unreachable — going offline');
      markOffline();
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        '[OfflineManager] request failed but origin reachable — staying online'
      );
    }
  } finally {
    confirming = false;
  }
}

async function refreshHomeCachedFlag() {
  if (typeof caches === 'undefined') {
    state.homeCached = false;
    return;
  }
  try {
    const match = await caches.match(HOME_PATH);
    state.homeCached = !!match;
  } catch (e) {
    state.homeCached = false;
  }
}

function stopAutoRetry() {
  if (autoRetryTimer) {
    clearInterval(autoRetryTimer);
    autoRetryTimer = null;
  }
}

function startAutoRetry() {
  stopAutoRetry();
  autoRetryTimer = setInterval(async () => {
    if (!state.isOffline || state.checking) return;
    const reachable = await probe();
    if (reachable) markOnline();
  }, AUTO_RETRY_INTERVAL_MS);
}

function markOffline() {
  if (state.isOffline) return;
  // eslint-disable-next-line no-console
  console.warn('[OfflineManager] markOffline -> showing OfflineScreen');
  state.isOffline = true;
  refreshHomeCachedFlag();
  startAutoRetry();
}

function markOnline() {
  const wasOffline = state.isOffline;
  state.isOffline = false;
  state.checking = false;
  stopAutoRetry();

  if (wasOffline) {
    // Reload the page the user was on so any data that failed to load
    // while offline is fetched fresh — no manual refresh required.
    // eslint-disable-next-line no-console
    console.warn('[OfflineManager] markOnline -> window.location.reload');
    window.location.reload();
  }
}

/**
 * Manually triggered from the offline screen's retry button.
 * Returns true if connectivity was restored, false otherwise.
 */
async function retry() {
  if (state.checking) return false;
  state.checking = true;
  const reachable = await probe();
  state.checking = false;
  if (reachable) {
    markOnline();
    return true;
  }
  refreshHomeCachedFlag();
  return false;
}

function goHome() {
  if (!router) return;
  if (router.currentRoute.value.path !== HOME_PATH) {
    router.push(HOME_PATH).catch(() => {});
  }
}

/**
 * Registers an axios instance so network-level failures (no response
 * received at all — connection dropped, DNS failure, etc.) flip the
 * offline state immediately, without waiting for a browser online/offline
 * event that may never fire (e.g. wifi connected but no internet).
 */
function attachAxios(axiosInstance) {
  axiosInstance.interceptors.response.use(
    (response) => {
      // A successful response proves the connection is actually usable.
      if (state.isOffline) markOnline();
      return response;
    },
    (error) => {
      // No response received at all (connection dropped, DNS failure,
      // server unreachable) *may* mean the network isn't usable — but a
      // single failed cross-origin signed request is not enough to prove it
      // (see confirmOfflineFromNetworkError). Verify against a same-origin
      // probe before blacking out the app, otherwise a rejected preflight on
      // one api.moustafahosney.com call would loop the whole WebView offline.
      const isNetworkError = !error.response && error.code !== 'ERR_CANCELED';
      if (isNetworkError) confirmOfflineFromNetworkError();
      return Promise.reject(error);
    }
  );
}

function init(routerInstance) {
  if (initialized) return;
  initialized = true;
  router = routerInstance || null;

  window.addEventListener('online', () => {
    // Don't trust the event blindly — verify real reachability first.
    retry();
  });
  window.addEventListener('offline', markOffline);

  if (state.isOffline) {
    refreshHomeCachedFlag();
    startAutoRetry();
  }
}

export const offlineState = readonly(state);

export const OfflineManager = {
  init,
  retry,
  goHome,
  attachAxios,
  state: offlineState,
};

export default OfflineManager;
